import {
  defineStore,
  ref,
  useRuntimeConfig,
  useUserStore,
} from '#imports';

export interface ProductSubscriptionOptions {
  duration: string;
  current: string;
  sale: string|null;
  old: string|null;
  currentExTax: string;
  saleExTax: string|null;
  oldExTax: string|null;
  percentageDiscount: number|null;
}
export interface PriceMap {
  current: string;
  sale: string|null;
  old: string|null;
  currentExTax: string;
  saleExTax: string|null;
  oldExTax: string|null;
  percentageDiscount: number|null;
  productSubscriptionOptions: ProductSubscriptionOptions|null;
}

export const usePriceStore = defineStore('price', () => {
  const priceMapping = ref(new Map<number, PriceMap>());
  const priceQueue = ref([]);
  const timerActive = ref(false);
  const fetchedCountry = ref<string>('');
  const promiseList = ref([]);

  async function getPrice (productId: number|string, refresh: boolean): Promise<PriceMap> {
    const userStore = useUserStore();

    if (refresh) {
      // clear stored prices on country change
      priceMapping.value.clear();
    }

    if (userStore.getSelectedCountry !== fetchedCountry.value) {
      fetchedCountry.value = userStore.getSelectedCountry;
    }

    const idAsNumber = parseInt(productId, 10);

    if (!priceMapping.value.has(idAsNumber)) {
      await queuePrice(idAsNumber);
    }

    return priceMapping.value.get(idAsNumber);
  }

  async function queuePrice (productId: number): Promise<void> {
    priceQueue.value.push(productId);

    if (!timerActive.value) {
      timerActive.value = true;

      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          const ids = priceQueue.value.join(',');
          const localResolves = promiseList.value;
          promiseList.value = [];
          timerActive.value = false;
          priceQueue.value = [];
          fetchPrices(ids)
            .then(() => {
              localResolves.forEach((otherResolve) => otherResolve());
              resolve();
            })
            .catch(reject);
        }, 100);
      })
    } else {
      // Store the promise in the array to be resolved by the first calling action
      return new Promise((resolve) => {
        promiseList.value.push(resolve);
      });
    }
  }

  async function fetchPrices (ids: string): Promise<void> {
    const config = useRuntimeConfig();
    const uri = '/wp-json/content-search/v1/search?type=product-pricing';
    const response = await fetch(`${config.public.baseUrl}${uri}&product-ids=${ids}&country-code=${fetchedCountry.value ?? 'US'}`);
    const data = await response.json();

    Object.values(data.results).forEach((entry) => {
      let productSubscriptionOptions = ! entry.product_subscription_options ? null : {
        duration: entry.product_subscription_options.duration,
        percentageDiscount: entry.product_subscription_options.percentageDiscount,
        current: entry.product_subscription_options.price,
        old: entry.product_subscription_options.regularPrice,
        sale: entry.product_subscription_options.salePrice,
        currentExTax: entry.product_subscription_options.priceExTax,
        oldExTax: entry.product_subscription_options.regularPriceExTax,
        saleExTax: entry.product_subscription_options.salePriceExTax,
      }

      priceMapping.value.set(parseInt(entry.id, 10), {
        current: entry.price_inc_tax,
        old: entry.regular_price_inc_tax,
        sale: entry.sale_price_inc_tax,
        currentExTax: entry.price_ex_tax,
        oldExTax: entry.regular_price_ex_tax,
        saleExTax: entry.sale_price_ex_tax,
        percentageDiscount: entry.percentage_discount,
        productSubscriptionOptions: productSubscriptionOptions,
      });
    });
  }

  return {
    getPrice,
  }
});
